import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
} from "@mui/material";
import { get_admin_stats } from "../Api service/APIvariables";
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import MoneyOffRoundedIcon from '@mui/icons-material/MoneyOffRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import ContactMailRoundedIcon from '@mui/icons-material/ContactMailRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import DashboardCustomizeRoundedIcon from '@mui/icons-material/DashboardCustomizeRounded';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

export default function AdminDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [stats, setStats] = useState({
    total_users: 0,
    active_paid_users: 0,
    free_users: 0,
    revenue_this_month: 0,
    total_revenue: 0,
    cards_scanned_today: 0,
    emails_sent_today: 0,
    total_cards_scanned: 0,
    total_emails_sent: 0,
    new_users_this_month: 0,
    failed_emails: 0,
    total_templates_built: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchStats = async () => {
    try {
      const user_id = localStorage.getItem("user_id");
      const token = localStorage.getItem("auth_token");
      if (!user_id || !token) return;

      const formData = new FormData();
      formData.append("user_id", user_id);

      const res = await fetch(get_admin_stats, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!data.error) {
        setStats(data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', mt: 4 }}>
        <LinearProgress color="secondary" />
      </Box>
    );
  }

  // Reusable card for the clean QuickCard CRM vibe
  const MetricCard = ({ title, value, icon, color }) => (
    <Card elevation={0} sx={{ height: "100%", borderRadius: "12px", border: "1px solid #e0e0e0", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3, '&:last-child': { pb: 3 } }}>
        <Box sx={{ p: 1.5, borderRadius: "8px", backgroundColor: `${color}15`, color: color, mr: 2, display: 'flex' }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="textSecondary" fontWeight="600" sx={{ mb: 0.5, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold" color="#333">
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: isMobile ? 1 : 2, width: "100%", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header section matching QuickCard CRM styling */}
      <Box sx={{ mb: 4, display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", backgroundColor: "white", p: 3, borderRadius: "12px", border: "1px solid #e0e0e0", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
        <Box>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="#453c72" sx={{ mb: 0.5 }}>
            Admin Overview Dashboard
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Monitor overall platform health, financials, and user engagement.
          </Typography>
        </Box>
        <Box sx={{ mt: isMobile ? 2 : 0, textAlign: isMobile ? "left" : "right", borderLeft: isMobile ? "none" : "2px solid #f0f0f0", pl: isMobile ? 0 : 3 }}>
          <Typography variant="body2" color="textSecondary" fontWeight="600" sx={{ textTransform: "uppercase" }}>
            Admin Status
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="#27ae60">
            SYSTEM ONLINE
          </Typography>
        </Box>
      </Box>

      {/* User Demographics Section */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: "#607d8b", textTransform: "uppercase", letterSpacing: 1 }}>User Demographics</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <MetricCard title="Total Users" value={stats.total_users} icon={<PeopleAltRoundedIcon sx={{ fontSize: 24 }} />} color="#1976d2" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricCard title="Active Paid Users" value={stats.active_paid_users} icon={<StarRoundedIcon sx={{ fontSize: 24 }} />} color="#9c27b0" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricCard title="Free Users" value={stats.free_users} icon={<MoneyOffRoundedIcon sx={{ fontSize: 24 }} />} color="#757575" />
        </Grid>
      </Grid>

      {/* Financial Overview Section */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: "#607d8b", textTransform: "uppercase", letterSpacing: 1 }}>Financial Overview</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <MetricCard title="Revenue This Month" value={`₹${parseFloat(stats.revenue_this_month).toLocaleString()}`} icon={<TrendingUpRoundedIcon sx={{ fontSize: 24 }} />} color="#2e7d32" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MetricCard title="Total Revenue" value={`₹${parseFloat(stats.total_revenue).toLocaleString()}`} icon={<AccountBalanceWalletRoundedIcon sx={{ fontSize: 24 }} />} color="#e65100" />
        </Grid>
      </Grid>

      {/* Platform Usage Today Section */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: "#607d8b", textTransform: "uppercase", letterSpacing: 1 }}>Platform Usage (Today)</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <MetricCard title="Cards Scanned Today" value={stats.cards_scanned_today} icon={<ContactMailRoundedIcon sx={{ fontSize: 24 }} />} color="#00838f" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MetricCard title="Emails Sent Today" value={stats.emails_sent_today} icon={<EmailRoundedIcon sx={{ fontSize: 24 }} />} color="#37474f" />
        </Grid>
      </Grid>

      {/* Platform Usage Overall Section */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: "#607d8b", textTransform: "uppercase", letterSpacing: 1 }}>Platform Usage (Overall)</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <MetricCard title="Total Cards Scanned" value={stats.total_cards_scanned} icon={<ContactMailRoundedIcon sx={{ fontSize: 24 }} />} color="#283593" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MetricCard title="Total Emails Sent" value={stats.total_emails_sent} icon={<EmailRoundedIcon sx={{ fontSize: 24 }} />} color="#512da8" />
        </Grid>
      </Grid>

      {/* Growth & System Health Section */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: "#607d8b", textTransform: "uppercase", letterSpacing: 1 }}>Growth & System Health</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <MetricCard title="New Users (This Month)" value={stats.new_users_this_month} icon={<PersonAddAlt1RoundedIcon sx={{ fontSize: 24 }} />} color="#2e7d32" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricCard title="Failed Emails" value={stats.failed_emails} icon={<ErrorOutlineRoundedIcon sx={{ fontSize: 24 }} />} color="#c62828" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricCard title="Total Templates Built" value={stats.total_templates_built} icon={<DashboardCustomizeRoundedIcon sx={{ fontSize: 24 }} />} color="#ef6c00" />
        </Grid>
      </Grid>

    </Box>
  );
}
